import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, NavLink } from 'react-router-dom';
import { useSite } from '../hooks/useSites';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { api } from '../api/client';
import { getSchema } from '../api/schemas';
import FormField from '../components/FormField';
import ToastContainer from '../components/Toast';
import { getContentId, type FieldSchema } from '../types';
import { TopziteOreIcon } from '../components/TopziteOreIcon';
import HeroButtonPreview from '../components/HeroButtonPreview';
import '../styles/onboarding.css';

const STEP_CONFIG = [
  {
    title: 'Set up your homepage',
    subtitle: (siteName: string) => `This is the first thing visitors see when they land on ${siteName}. Make it count!`,
    contentType: 'hero',
    emoji: '✨',
  },
  {
    title: 'Tell people about yourself',
    subtitle: () => 'Share your story — who you are and what makes you special.',
    contentType: 'about',
    emoji: '🎨',
  },
  {
    title: 'How can people reach you?',
    subtitle: () => 'Add your contact details so customers can get in touch.',
    contentType: 'contact',
    emoji: '📬',
  },
];

/** True if the user has entered anything worth saving. */
function hasAnyInput(data: Record<string, unknown>): boolean {
  return Object.values(data).some((v) =>
    typeof v === 'string' ? v.trim() !== '' : v != null,
  );
}

export default function Onboarding() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { site, loading: siteLoading } = useSite(siteId);
  const { addToast } = useToast();

  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [existingId, setExistingId] = useState<string | null>(null);
  const [stepLoading, setStepLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load any existing content when a step opens, so revisiting a step shows
  // what was saved (and Next can never overwrite saved data with an empty form)
  const loadStep = useCallback(async () => {
    if (!siteId) return;
    setStepLoading(true);
    setFormData({});
    setExistingId(null);
    try {
      const existing = await api.listContent(siteId, STEP_CONFIG[step].contentType);
      if (existing.length > 0) {
        setFormData(existing[0].data || {});
        setExistingId(getContentId(existing[0].contentKey));
      }
    } catch {
      // Treated as no existing content — the step starts blank
    } finally {
      setStepLoading(false);
    }
  }, [siteId, step]);

  useEffect(() => {
    loadStep();
  }, [loadStep]);

  // Auth redirect
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, user, navigate]);

  if (!authLoading && !user) {
    return null;
  }

  if (siteLoading || authLoading) {
    return (
      <div className="onboarding-layout">
        <header className="topbar">
          <NavLink to="/dashboard" className="topbar-brand">
            <TopziteOreIcon size={24} />
            <span className="topbar-brand-text">top<span className="topbar-brand-accent">zite</span></span>
          </NavLink>
        </header>
        <div className="loading-state">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (!site || !siteId) {
    return (
      <div className="onboarding-layout">
        <header className="topbar">
          <NavLink to="/dashboard" className="topbar-brand">
            <TopziteOreIcon size={24} />
            <span className="topbar-brand-text">top<span className="topbar-brand-accent">zite</span></span>
          </NavLink>
        </header>
        <div className="error-state">
          <p>Site not found</p>
        </div>
      </div>
    );
  }

  const schema = getSchema(site.templateId);
  const currentStep = STEP_CONFIG[step];
  const contentTypeSchema = schema?.contentTypes[currentStep.contentType];
  const fields = contentTypeSchema
    ? Object.entries(contentTypeSchema.fields).filter(([, f]) => !f.hidden)
    : [];

  const siteName = site.siteName || 'your site';

  const handleFieldChange = (name: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!siteId) return;

    // Nothing entered and nothing saved before — just move on,
    // don't create an empty content record
    if (!existingId && !hasAnyInput(formData)) {
      advance();
      return;
    }

    setSaving(true);
    try {
      if (existingId) {
        await api.updateContent(siteId, currentStep.contentType, existingId, formData);
      } else {
        await api.createContent(siteId, currentStep.contentType, formData);
      }
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to save');
      setSaving(false);
      return;
    }
    setSaving(false);
    advance();
  };

  const advance = () => {
    if (step < STEP_CONFIG.length - 1) {
      setStep(step + 1);
    } else {
      addToast('success', 'Your site is ready! Add some products and deploy when you\'re ready.');
      navigate(`/sites/${siteId}`);
    }
  };

  const handleSkip = () => {
    advance();
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const isLastStep = step === STEP_CONFIG.length - 1;
  const progressPercent = ((step + 1) / STEP_CONFIG.length) * 100;

  return (
    <div className="onboarding-layout">
      <header className="topbar">
        <NavLink to="/dashboard" className="topbar-brand">
          <TopziteOreIcon size={24} />
          <span className="topbar-brand-text">top<span className="topbar-brand-accent">zite</span></span>
        </NavLink>
        <div className="topbar-user">
          <span className="topbar-email">{user?.email}</span>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => navigate(`/sites/${siteId}`)}
          >
            Skip setup
          </button>
        </div>
      </header>

      <div className="onboarding-container">
        {/* Progress bar */}
        <div className="onboarding-progress">
          <div className="onboarding-progress-bar">
            <div
              className="onboarding-progress-fill"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <span className="onboarding-progress-label">
            {siteName} — Step {step + 1} of {STEP_CONFIG.length}
          </span>
        </div>

        {/* Step content */}
        <div className="onboarding-card">
          <div className="onboarding-step-header">
            {currentStep.emoji && (
              <span className="onboarding-step-emoji">{currentStep.emoji}</span>
            )}
            <h1 className="onboarding-step-title">{currentStep.title}</h1>
            <p className="onboarding-step-subtitle">{currentStep.subtitle(siteName)}</p>
          </div>

          {stepLoading ? (
            <div className="loading-state" style={{ padding: '48px 0' }}>
              <div className="spinner" />
            </div>
          ) : (
          <div className="onboarding-form">
            {fields.map(([name, fieldSchema]) => {
              const schema = fieldSchema as FieldSchema;
              // Add (optional) to label for non-required fields
              const labelSuffix = schema.required ? '' : ' (optional)';
              return (
                <div key={name}>
                  {/* Show visual explainer before button fields on the hero step */}
                  {name === 'ctaText' && currentStep.contentType === 'hero' && (
                    <HeroButtonPreview />
                  )}
                  <FormField
                    name={name}
                    schema={{ ...schema, label: `${schema.label}${labelSuffix}` }}
                    value={formData[name] ?? ''}
                    onChange={handleFieldChange}
                    siteId={siteId}
                  />
                </div>
              );
            })}
          </div>
          )}

          <div className="onboarding-actions">
            <div className="onboarding-actions-left">
              {step > 0 && (
                <button className="btn btn-secondary" onClick={handleBack}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  Back
                </button>
              )}
              <button className="btn-text" onClick={handleSkip}>
                Skip this step
              </button>
            </div>
            <button
              className="btn btn-primary btn-lg"
              onClick={handleSave}
              disabled={saving || stepLoading}
            >
              {saving ? 'Saving…' : isLastStep ? 'Finish' : (
                <>
                  Next
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
}
